import React, { useState, useEffect, useRef, useCallback } from 'react';
import appwrite, { COLLECTIONS } from '../../services/appwrite';
import { softDelete } from '../../services/records';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'sonner';
import { Trash2 } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import DeleteConfirmDialog from '../../components/dialogs/DeleteConfirmDialog';

/**
 * Note form using Dialog pattern (matching app conventions)
 * Creates note on first keystroke, syncs in realtime
 */
const NoteForm = ({ 
  note: initialNote = null, 
  recordType,
  recordId,
  isOpen, 
  onClose,
  onSave,
  onDelete
}) => {
  const { user, organisationId } = useAuth();
  const [content, setContent] = useState('');
  const [noteId, setNoteId] = useState(initialNote?.$id || null);
  const [isCreating, setIsCreating] = useState(false);
  const saveTimeoutRef = useRef(null);
  const isInitialMount = useRef(true);
  const unsubscribeRef = useRef(null);
  const isRealtimeUpdate = useRef(false);
  const contentRef = useRef('');
  // Keep contentRef in sync so createNote/updateNote always read latest value
  contentRef.current = content;

  useEffect(() => {
    if (initialNote) {
      setContent(initialNote.content || '');
      setNoteId(initialNote.$id);
    } else {
      setContent('');
      setNoteId(null);
    }
    isInitialMount.current = true;

    // Setup realtime subscription for existing notes
    if (initialNote?.$id) {
      const channel = `databases.flickquote.collections.${COLLECTIONS.NOTES}.documents.${initialNote.$id}`;
      
      const unsubscribe = appwrite.client.subscribe(channel, (response) => {
        if (response.events.includes('databases.*.collections.*.documents.*.update')) {
          isRealtimeUpdate.current = true;
          setContent(response.payload.content || '');
        }
      });

      unsubscribeRef.current = unsubscribe;
    }

    return () => {
      if (unsubscribeRef.current) {
        unsubscribeRef.current();
      }
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
    };
  }, [initialNote, isOpen]);

  const createNote = useCallback(async () => {
    if (isCreating || noteId) return;

    try {
      setIsCreating(true);
      const newNote = await appwrite.createDocument(
        COLLECTIONS.NOTES,
        {
          organisationId,
          content: contentRef.current.trim(),
          relatedRecordType: recordType,
          relatedRecordId: recordId,
          createdBy: user?.$id
        }
      );
      setNoteId(newNote.$id);
      onSave?.(newNote);

      // Setup realtime subscription for the new note
      const channel = `databases.flickquote.collections.${COLLECTIONS.NOTES}.documents.${newNote.$id}`;
      const unsubscribe = appwrite.client.subscribe(channel, (response) => {
        if (response.events.includes('databases.*.collections.*.documents.*.update')) {
          isRealtimeUpdate.current = true;
          setContent(response.payload.content || '');
        }
      });
      unsubscribeRef.current = unsubscribe;
    } catch (error) {
      console.error('Failed to create note:', error);
      toast.error('Failed to create note');
    } finally {
      setIsCreating(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- subscription callback intentionally omits content to avoid re-subscribing on every keystroke
  }, [isCreating, noteId, organisationId, recordType, recordId, user, onSave]);

  const updateNote = useCallback(async () => {
    if (!noteId) return;

    try {
      await appwrite.updateDocument(COLLECTIONS.NOTES, noteId, {
        content: contentRef.current.trim(),
        updatedBy: user?.$id
      });
    } catch (error) {
      console.error('Failed to update note:', error);
      toast.error('Failed to update note');
    }
  }, [noteId, user]);

  // Auto-save on content change with debounce
  useEffect(() => {
    // Skip if this change came from a realtime update (prevents save loop)
    if (isRealtimeUpdate.current) {
      isRealtimeUpdate.current = false;
      return;
    }

    // Skip on initial mount
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }

    // Clear existing timeout
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }

    // If no noteId yet, create note immediately
    if (!noteId && content.trim() && !isCreating) {
      createNote();
      return;
    }

    // If note exists, debounce updates
    if (noteId && content.trim()) {
      saveTimeoutRef.current = setTimeout(() => {
        updateNote();
      }, 500);
    }
  }, [content, noteId, isCreating, createNote, updateNote]);



  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const handleDelete = async () => {
    if (!noteId) return;

    try {
      // Soft: restorable for 90 days. See services/records.js.
      await softDelete(COLLECTIONS.NOTES, noteId);
      toast.success('Note deleted');
      onDelete?.(noteId);
      onClose();
    } catch (error) {
      console.error('Failed to delete note:', error);
      toast.error(error?.message || 'Failed to delete note');
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-2xl max-h-[80vh] flex flex-col">
        <DialogHeader>
          <div className="flex flex-col gap-1">
            <div className="flex items-center gap-2">
              <DialogTitle>
                {!noteId ? 'New Note' : 'Edit Note'}
              </DialogTitle>
              {noteId && (
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-7 w-7 text-muted-foreground hover:text-destructive"
                  title="Delete note"
                  onClick={() => setShowDeleteConfirm(true)}
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
            )}
            </div>
            {initialNote?.$createdAt && (
              <p className="text-sm text-muted-foreground">
                Created {new Date(initialNote.$createdAt).toLocaleDateString('en-NZ', {
                  day: 'numeric',
                  month: 'short',
                  year: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit'
                })}
              </p>
            )}
          </div>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto space-y-4">
          <div className="space-y-2">
            <Label htmlFor="note-content">Note</Label>
            <Textarea
              id="note-content"
              value={content}
              onChange={(e) => setContent(e.target.value)}
              placeholder="Start typing to create a note..."
              className="min-h-[300px] resize-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
              autoFocus
            />
          </div>

          {initialNote?.$updatedAt && initialNote.$updatedAt !== initialNote.$createdAt && (
            <div className="text-sm text-muted-foreground">
              Last updated {new Date(initialNote.$updatedAt).toLocaleDateString('en-NZ', {
                day: 'numeric',
                month: 'short',
                year: 'numeric',
                hour: '2-digit',
                minute: '2-digit'
              })}
            </div>
          )}


        </div>
        <DeleteConfirmDialog
          open={showDeleteConfirm}
          onOpenChange={setShowDeleteConfirm}
          onConfirm={handleDelete}
        />
      </DialogContent>
    </Dialog>
  );
};

export default NoteForm;
